import styled from 'styled-components'
import React, { useRef, useState } from 'react'

import { EthereumAddressInput } from './EthereumAddressInput/EthereumAddressInput'
import { ExampleTokens } from './ExampleTokens'
import { useTokenBalance } from '../hooks/useTokenBalance'
import useENS from '../hooks/useEns'
import { TextInputHandle } from './TextInput/TextInput'

const Wrapper = styled.div`
  padding: 1rem;
  display: flex;
  gap: 0.5rem;
  background: #fff;
  border-radius: 1rem;
  flex-direction: column;
  width: 100%;
  text-align: center;
`
const Balance = styled.div`
  text-align: center;
`

const SubmitButton = styled.button.attrs({ type: 'button' })`
  background: #00b38a;
  color: #fff;
  height: 2.5rem;
  border: none;
  border-radius: 2.5rem;
  padding: 0.4rem 1rem;
  font-weight: bold;
  cursor: pointer;

  &:hover {
    background: #01a680;
  }

  &:active {
    background: #018365;
  }
`

const ErrorMessage = styled.p`
  color: #eb258b;
  font-size: 0.8rem;
`

const TokenSymbol = styled.small`
  font-size: 0.8rem;
`

const Code = styled.code`
  font-size: 0.8rem;
`

const Separator = styled.hr`
  border: 0;
  border-top: 1px solid #eee;
  height: 1px;
  padding: 0;
  margin: 0;
`

type ENSResolutionResult = {
  isReverse?: boolean
  input?: string
  output?: string
}

const AccountInfo: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [tokenAddress, setTokenAddress] = useState('')
  const [accountAddress, setAccountAddress] = useState('')
  const [error, setError] = useState(false)
  
  const [ensResolution, setEnsResolution] = useState<ENSResolutionResult | null>(null)
  
  const { lookup, reverseLookup, isENSDomain } = useENS()
  const { balance, error: balanceError, symbol, getBalance, clearState } = useTokenBalance()
  const tokenInputRef = useRef<TextInputHandle | null>(null)
  
  const handleTokenChoice = (token: string) => {
    tokenInputRef?.current?.setValue(token)
  }

  const handleAccountChange = (account: string) => {
    if (account !== accountAddress) {
      clearState()
    }

    setAccountAddress(account)
  }

  const handleSubmit = async () => {
    clearState()
    setEnsResolution(null)
    setIsLoading(true)
    setError(false)

    try {
      if (isENSDomain(accountAddress)) {
        const ensResolved = await lookup(accountAddress)

        await getBalance(tokenAddress, ensResolved)

        setEnsResolution({
          isReverse: false,
          input: accountAddress,
          output: ensResolved
        })
      } else {
        // FIXME: if one promise rejects, we won't show any results.
        const [, foundEnsDomain] = await Promise.all([
          getBalance(tokenAddress, accountAddress),
          reverseLookup(accountAddress)
        ])

        setEnsResolution({
          isReverse: true,
          input: accountAddress,
          output: foundEnsDomain
        })
      }
    } catch (ex) {
      console.error(ex)
      setError(true)
    } finally {
      setIsLoading(false)
    }

  }

  return (
    <Wrapper>
      <EthereumAddressInput
        ref={tokenInputRef}
        label="ERC20 token address"
        defaultValue={tokenAddress}
        onChange={(providedAddress) => setTokenAddress(providedAddress)}
      />
      <small>or choose one from the list below</small>
      <ExampleTokens onChange={handleTokenChoice} address={tokenAddress} />
      <EthereumAddressInput
        label="Account address"
        onChange={handleAccountChange}
        ensSupport
      />
      <SubmitButton type="button" onClick={handleSubmit} disabled={isLoading}>{isLoading ? 'Loading...' : 'Fetch info'}</SubmitButton>
      {!balanceError && balance && symbol && (
        <Balance>
          <p>This account currently holds</p>
          <h2>{balance?.toFixed(4)} <TokenSymbol>{symbol}</TokenSymbol></h2>
          <div></div>
        </Balance>
      )}
      {(error || balanceError) && (
        <ErrorMessage>An unexpected error occured while fetching account information. Please try again later.</ErrorMessage>
      )}
      {ensResolution && ensResolution.input === accountAddress && ensResolution.output && (
        <>
          <Separator />
          <small>{ensResolution.isReverse ? 'This address is registered under ENS domain:' : 'This ENS domain resolves to:'}</small>
          <Code>{ensResolution.output}</Code>
        </>
      )}
    </Wrapper>
  )
}

export default AccountInfo
